/**
 * Record Service
 * Service for managing records - uses Afikim Platform API
 * Supports both old and new platform endpoints
 */
import config from '../config/config';

const API_BASE = config.api.baseUrl;
const LOG_PREFIX = '[RecordService]';
let requestCounter = 0;

// Use new platform API or legacy
const USE_PLATFORM_API = false;  // Platform API not implemented yet - use legacy
const PLATFORM_PREFIX = '/api/platform';
const LEGACY_PREFIX = '/api/objects';

// Logging helpers
const logRequest = (method, endpoint, requestId) => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  console.log(`${timestamp} | ${LOG_PREFIX} [${requestId}] >>> ${method} ${endpoint}`);
};

const logResponse = (requestId, status, duration, recordCount = null) => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const icon = status < 400 ? '✓' : '✗';
  const countStr = recordCount !== null ? ` | ${recordCount} records` : '';
  console.log(`${timestamp} | ${LOG_PREFIX} [${requestId}] <<< ${icon} ${status} (${duration}ms)${countStr}`);
};

const logError = (requestId, error, duration) => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  console.error(`${timestamp} | ${LOG_PREFIX} [${requestId}] !!! ERROR: ${error.message} (${duration}ms)`);
};

class RecordService {
  constructor() {
    this.endpoint = USE_PLATFORM_API ? `${PLATFORM_PREFIX}/sobjects` : LEGACY_PREFIX;
    this.connectionStatus = 'unknown';
    this.failedAttempts = 0;
    this.currentUser = null;
    console.log(`${LOG_PREFIX} Initialized | API Base: ${API_BASE} | Platform API: ${USE_PLATFORM_API}`);
  }

  /**
   * Set current user for tracking
   */
  setCurrentUser(user) {
    this.currentUser = user;
    console.log(`${LOG_PREFIX} Current user set:`, user?.name || user?.email || 'Unknown');
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Internal fetch with logging
   */
  async _fetch(url, options = {}) {
    const requestId = String(++requestCounter).padStart(4, '0');
    const method = options.method || 'GET';
    const startTime = performance.now();

    const endpoint = url.replace(API_BASE, '');
    logRequest(method, endpoint, requestId);

    try {
      const response = await fetch(url, options);
      const duration = Math.round(performance.now() - startTime);

      if (!response.ok) {
        logResponse(requestId, response.status, duration);
        this.failedAttempts++;
        this.connectionStatus = 'error';
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const recordCount = data.records ? data.records.length : (data.record ? 1 : null);
      logResponse(requestId, response.status, duration, recordCount);

      // Log performance info from platform
      if (data._performance) {
        console.log(`${LOG_PREFIX} [${requestId}] Platform: ${data._performance.query_time_ms}ms`);
      }

      this.connectionStatus = 'connected';
      this.failedAttempts = 0;
      return data;

    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      logError(requestId, error, duration);

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        this.connectionStatus = 'disconnected';
        this.failedAttempts++;
        console.error(`${LOG_PREFIX} Network error - Server unreachable. Failed attempts: ${this.failedAttempts}`);
      }

      throw error;
    }
  }

  /**
   * Get records for an object
   */
  async getRecords(objectName, options = {}) {
    const {
      page = 1,
      pageSize = 25,
      search = null,
      orderBy = null,
      fields = null
    } = options;

    const offset = (page - 1) * pageSize;

    console.log(`${LOG_PREFIX} Fetching ${objectName} records (page ${page}, size ${pageSize})`);

    let url;
    if (USE_PLATFORM_API) {
      // New platform API
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString()
      });
      if (search) params.append('search', search);
      if (orderBy) params.append('order_by', orderBy);
      if (fields) params.append('fields', Array.isArray(fields) ? fields.join(',') : fields);

      url = `${API_BASE}${this.endpoint}/${objectName}/records?${params}`;
    } else {
      // Legacy API
      url = `${API_BASE}${this.endpoint}/${objectName}/records?page=${page}&page_size=${pageSize}`;
    }

    const data = await this._fetch(url);

    // Normalize response format
    return {
      records: data.records || [],
      total: data.totalSize || data.total || data.records?.length || 0,
      page,
      pageSize,
      performance: data._performance
    };
  }

  /**
   * Execute AQL query (Platform API only)
   */
  async executeAQL(query) {
    if (!USE_PLATFORM_API) {
      throw new Error('AQL queries require Platform API');
    }

    console.log(`${LOG_PREFIX} Executing AQL: ${query.substring(0, 100)}...`);

    const data = await this._fetch(
      `${API_BASE}${PLATFORM_PREFIX}/query?q=${encodeURIComponent(query)}`
    );

    return {
      records: data.records || [],
      totalSize: data.totalSize || 0,
      done: data.done !== false,
      performance: data._performance
    };
  }

  /**
   * Get single record
   */
  async getRecord(objectName, recordId) {
    console.log(`${LOG_PREFIX} Fetching single record: ${objectName}/${recordId}`);

    const data = await this._fetch(
      `${API_BASE}${this.endpoint}/${objectName}/records/${recordId}?resolve_refs=true`
    );

    // Platform API returns record directly, legacy wraps in .record
    return data.record || data;
  }

  /**
   * Create record
   */
  async createRecord(objectName, data) {
    console.log(`${LOG_PREFIX} Creating ${objectName} record`);

    // Add user tracking info if available
    const recordData = { ...data };
    if (this.currentUser) {
      if (!recordData.CreatedById) {
        recordData.CreatedById = this.currentUser.id || this.currentUser.Id;
      }
      if (!recordData.CreatedByName) {
        recordData.CreatedByName = this.currentUser.name || this.currentUser.Name || this.currentUser.email;
      }
      if (!recordData.LastModifiedById) {
        recordData.LastModifiedById = this.currentUser.id || this.currentUser.Id;
      }
      if (!recordData.LastModifiedByName) {
        recordData.LastModifiedByName = this.currentUser.name || this.currentUser.Name || this.currentUser.email;
      }
    }

    const result = await this._fetch(
      `${API_BASE}${this.endpoint}/${objectName}/records`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recordData)
      }
    );

    return {
      id: result.id,
      success: result.success !== false,
      record: result.record || result,
      performance: result._performance
    };
  }

  /**
   * Update record
   */
  async updateRecord(objectName, recordId, updates) {
    console.log(`${LOG_PREFIX} Updating record: ${objectName}/${recordId}`);

    // Add user tracking info if available
    const updateData = { ...updates };
    if (this.currentUser) {
      updateData.LastModifiedById = this.currentUser.id || this.currentUser.Id;
      updateData.LastModifiedByName = this.currentUser.name || this.currentUser.Name || this.currentUser.email;
    }

    // Platform API uses PATCH, legacy uses PUT
    const method = USE_PLATFORM_API ? 'PATCH' : 'PUT';

    const result = await this._fetch(
      `${API_BASE}${this.endpoint}/${objectName}/records/${recordId}`,
      {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      }
    );

    return {
      id: result.id || recordId,
      success: result.success !== false,
      record: result.record || result,
      performance: result._performance
    };
  }

  /**
   * Delete record
   */
  async deleteRecord(objectName, recordId) {
    console.log(`${LOG_PREFIX} Deleting record: ${objectName}/${recordId}`);

    return await this._fetch(
      `${API_BASE}${this.endpoint}/${objectName}/records/${recordId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Search across multiple objects (Platform API only)
   */
  async globalSearch(searchTerm, objects = null, limit = 20) {
    if (!USE_PLATFORM_API) {
      throw new Error('Global search requires Platform API');
    }

    console.log(`${LOG_PREFIX} Global search: "${searchTerm}"`);

    const params = new URLSearchParams({
      q: searchTerm,
      limit: limit.toString()
    });
    if (objects) {
      params.append('sobjects', Array.isArray(objects) ? objects.join(',') : objects);
    }

    const data = await this._fetch(
      `${API_BASE}${PLATFORM_PREFIX}/search?${params}`
    );

    return {
      searchRecords: data.searchRecords || [],
      totalSize: data.totalSize || 0,
      performance: data._performance
    };
  }

  /**
   * Get related records
   */
  async getRelatedRecords(objectName, recordId, relationshipName) {
    console.log(`${LOG_PREFIX} Fetching related: ${objectName}/${recordId}/${relationshipName}`);

    if (USE_PLATFORM_API) {
      // Use AQL to get related records
      const query = `SELECT Id, Name FROM ${relationshipName} WHERE ${objectName}Id = '${recordId}' LIMIT 100`;
      try {
        const result = await this.executeAQL(query);
        return result.records;
      } catch (e) {
        console.warn(`${LOG_PREFIX} Related records query failed, trying alternative:`, e);
        // Try alternative relationship query
        return [];
      }
    }

    return [];
  }

  /**
   * Get object metadata
   */
  async describeObject(objectName) {
    console.log(`${LOG_PREFIX} Describing object: ${objectName}`);

    if (USE_PLATFORM_API) {
      return await this._fetch(
        `${API_BASE}${PLATFORM_PREFIX}/sobjects/${objectName}`
      );
    }

    // Legacy fallback
    return null;
  }

  /**
   * List all objects
   */
  async describeGlobal() {
    console.log(`${LOG_PREFIX} Describing all objects`);

    if (USE_PLATFORM_API) {
      return await this._fetch(
        `${API_BASE}${PLATFORM_PREFIX}/sobjects`
      );
    }

    // Legacy fallback
    return null;
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      status: this.connectionStatus,
      failedAttempts: this.failedAttempts,
      apiBase: API_BASE,
      usePlatformAPI: USE_PLATFORM_API
    };
  }

  /**
   * Get platform status
   */
  async getPlatformStatus() {
    if (!USE_PLATFORM_API) return null;

    return await this._fetch(
      `${API_BASE}${PLATFORM_PREFIX}/status`
    );
  }
}

export const recordService = new RecordService();
export default recordService;
